import uniq from 'lodash-es/uniq.js';
import { memoEval } from '@salsita-ai/platform-project-sdk/domain';
import { $Hole } from '../__generated/enhanced-model.js';
import { implementCustomExpressions, implementCustomInvariants } from '../__generated/implementation.js';
import { BendDirection, BendOrientation, Hole, KickPlateFixingHoles, StrikePlate, StrikePlateShapeType, StrikeTonguePosition } from '../__generated/model/index.js';
import { createPD7HolePolygon } from '../polygons/pd7-hole.js';
import { createPN6HolePolygon } from '../polygons/pn6-hole.js';
import { createRectangularHolePolygon } from '../polygons/rectangular-hole.js';
import { createSquareHolePolygon } from '../polygons/square-hole.js';
import { generateBoltHoleRecipes, generateCountersinkHoleRecipes } from '../recipes/hole.recipes.js';
import { rotateVectorOntoBentArm } from '../util/bend-vector.js';
import { round } from '../util/round.js';
import { scaleIndicator } from './plate.js';
export const holeLabels = {
    BoltHole: 'Bolt Hole',
    CountersinkHole: 'Countersink Hole',
    CornerBoltHoles: 'Bolt Hole (All Corners)',
    CornerCountersinkHoles: 'Countersink Hole (All Corners)',
    KickPlateBoltHoles: 'Bolt Holes',
    KickPlateCountersinkHoles: 'Countersink Holes',
    CamlockHole: 'Camlock Hole',
    DeadboltHole: 'Deadbolt Hole',
    PN6Hole: 'PN6 Lockset Hole',
    PD7Hole: 'PD7 Lockset Hole',
    EuroCylinderHoleAu: 'Euro Cylinder Hole',
    EuroCylinderHoleEu: 'Euro Cylinder Hole',
    Hole54: '54 mm Lockset Hole',
    OvalCylinderHole: 'Oval Cylinder Hole',
    PlainCylinderHole: 'Cylinder Hole',
    PlainLocksetHole: 'Lockset Hole',
    RectangularHole: 'Rectangular Hole',
    RimCylinderHole: 'Rim Cylinder Hole',
    SquareHole: 'Square Hole'
};
const sizeTolerance = {
    x: 0,
    y: 0
};
/**
 * Size indicators should show nominal dimensions from recipes/properties.
 * Bounding boxes can be tolerance-adjusted for manufacturing logic.
 */ const getNominalSizeIndicatorBoundingBox3D = (self)=>{
    const widthHalf = self.$sizeX / 2;
    const heightHalf = self.$sizeY / 2;
    const bend = self.$plate.$activeBend;
    const absX = self.$absCoords.x;
    const absY = self.$absCoords.y;
    if (!bend || bend.orientation === BendOrientation.Horizontal && absY < bend.$middlePoint.y || bend.orientation === BendOrientation.Vertical && absX < bend.$middlePoint.x) {
        return {
            bottomLeft: {
                x: -widthHalf,
                y: -heightHalf,
                z: 0
            },
            topRight: {
                x: widthHalf,
                y: heightHalf,
                z: 0
            }
        };
    }
    const directionSign = bend.direction === BendDirection.Front ? 1 : -1;
    if (bend.orientation === BendOrientation.Horizontal) {
        return {
            bottomLeft: {
                x: -widthHalf,
                y: 0,
                z: -heightHalf * directionSign
            },
            topRight: {
                x: widthHalf,
                y: 0,
                z: heightHalf * directionSign
            }
        };
    }
    return {
        bottomLeft: {
            x: 0,
            y: -heightHalf,
            z: -widthHalf * directionSign
        },
        topRight: {
            x: 0,
            y: heightHalf,
            z: widthHalf * directionSign
        }
    };
};
const isHoleOnBentArm = (self)=>{
    const bend = self.$plate.$activeBend;
    return bend != null && (bend.orientation === BendOrientation.Horizontal && self.$absCoords.y >= bend.$middlePoint.y || bend.orientation === BendOrientation.Vertical && self.$absCoords.x >= bend.$middlePoint.x);
};
const getBendAwareExtrusionVector = (self, vector)=>{
    const bend = self.$plate.$activeBend;
    if (!bend || !isHoleOnBentArm(self)) {
        return vector;
    }
    return rotateVectorOntoBentArm(vector, bend.orientation, bend.direction);
};
const getSizeYIndicatorExtrusion = (self)=>{
    const bend = self.$plate.$activeBend;
    const minExtrusion = !bend || bend.orientation === BendOrientation.Vertical && isHoleOnBentArm(self) ? 10 : 5;
    return scaleIndicator(minExtrusion, 120, Math.max(self.$plate.$sizeX, self.$plate.$sizeY));
};
export const holeExpressions = implementCustomExpressions({
    Hole: {
        $absCoords: (self)=>{
            // self
            const fromPoint = self.$anchorPointFrom.$relCoords;
            // origin
            const toPoint = self.$anchorPointTo.$absCoords;
            return {
                x: toPoint.x + self.x - fromPoint.x,
                y: toPoint.y + self.y - fromPoint.y
            };
        },
        $absCoords3D: (self)=>{
            const { x: absX, y: absY } = self.$absCoords;
            const plate = self.$plate;
            const bend = plate.$activeBend;
            if (!bend || bend.orientation === BendOrientation.Horizontal && absY < bend.$middlePoint.y || bend.orientation === BendOrientation.Vertical && absX < bend.$middlePoint.x) {
                return {
                    x: absX,
                    y: absY,
                    z: 0
                };
            }
            const middlePoint = bend.$middlePoint;
            if (bend.orientation === BendOrientation.Horizontal) {
                const absZ = absY - middlePoint.y;
                return {
                    x: absX,
                    y: Math.min(absY, middlePoint.y),
                    z: bend.direction === BendDirection.Front ? -absZ : absZ
                };
            } else {
                const absZ = absX - middlePoint.x;
                return {
                    x: Math.min(absX, middlePoint.x),
                    y: absY,
                    z: bend.direction === BendDirection.Front ? -absZ : absZ
                };
            }
        },
        // $anchorPoints: self => self.$plate.$allAnchorPoints.filter(ap => ap.shapeId === self.id),
        $hiddenAnchorPoints: ()=>[],
        $anchorableShapesChain: (self)=>{
            const chain = [];
            let shape = self.$anchorPointTo.$shape;
            while(shape){
                if (!$Hole.is(shape)) {
                    break;
                }
                chain.push(shape);
                shape = shape.$anchorPointTo.$shape;
            }
            return chain;
        },
        $boundingBox: (self)=>{
            const sizeTolerance = self.$sizeTolerance;
            const widthHalf = self.$sizeX / 2 - sizeTolerance.x / 2;
            const heightHalf = self.$sizeY / 2 - sizeTolerance.y / 2;
            return {
                bottomLeft: {
                    x: -widthHalf,
                    y: -heightHalf
                },
                topRight: {
                    x: widthHalf,
                    y: heightHalf
                }
            };
        },
        $boundingBox3D: (self)=>{
            const plate = self.$plate;
            const bend = plate.$activeBend;
            const absX = self.$absCoords.x;
            const absY = self.$absCoords.y;
            const relBoundingBox = self.$boundingBox;
            let center = {
                x: 0,
                y: 0,
                z: 0
            };
            let topRight = {
                x: 0,
                y: 0,
                z: 0
            };
            let bottomLeft = {
                x: 0,
                y: 0,
                z: 0
            };
            if (!bend || bend.orientation === BendOrientation.Horizontal && absY < bend.$middlePoint.y || bend.orientation === BendOrientation.Vertical && absX < bend.$middlePoint.x) {
                center = {
                    x: self.$absCoords.x,
                    y: self.$absCoords.y,
                    z: 0
                };
                topRight = {
                    x: relBoundingBox.topRight.x,
                    y: relBoundingBox.topRight.y,
                    z: 0
                };
                bottomLeft = {
                    x: relBoundingBox.bottomLeft.x,
                    y: relBoundingBox.bottomLeft.y,
                    z: 0
                };
            } else {
                const middlePoint = bend.$middlePoint;
                if (bend.orientation === BendOrientation.Horizontal) {
                    const unsignedAbsZ = absY - middlePoint.y;
                    const directionSign = bend.direction === BendDirection.Front ? 1 : -1;
                    center = {
                        x: self.$absCoords.x,
                        y: middlePoint.y,
                        z: bend.direction === BendDirection.Front ? -unsignedAbsZ : unsignedAbsZ
                    };
                    bottomLeft = {
                        x: relBoundingBox.bottomLeft.x,
                        y: 0,
                        z: relBoundingBox.bottomLeft.y * directionSign
                    };
                    topRight = {
                        x: relBoundingBox.topRight.x,
                        y: 0,
                        z: relBoundingBox.topRight.y * directionSign
                    };
                } else {
                    const unsignedAbsZ = absX - middlePoint.x;
                    const directionSign = bend.direction === BendDirection.Front ? 1 : -1;
                    center = {
                        x: middlePoint.x,
                        y: 0,
                        z: unsignedAbsZ * directionSign
                    };
                    bottomLeft = {
                        x: 0,
                        y: relBoundingBox.bottomLeft.y,
                        z: relBoundingBox.bottomLeft.x * directionSign
                    };
                    topRight = {
                        x: 0,
                        y: relBoundingBox.topRight.y,
                        z: relBoundingBox.topRight.x * directionSign
                    };
                }
            }
            return {
                bottomLeft,
                topRight,
                center
            };
        },
        $center: ()=>({
                x: 0,
                y: 0
            }),
        $minX: (self)=>{
            const minX = -(self.$anchorPointTo.$absCoords.x - self.$anchorPointFrom.$relCoords.x - self.$sizeX / 2);
            const plate = self.$plate;
            if (StrikePlate.is(plate) && plate.$tongueBend && plate.tonguePosition === StrikeTonguePosition.left) {
                return Math.ceil(minX + plate.$minTongueHorizontalLength);
            }
            return minX - self.$plate.$minimumDistanceHoleToEdge;
        },
        $maxX: (self)=>{
            const maxX = self.$plate.$sizeX - self.$anchorPointTo.$absCoords.x + self.$anchorPointFrom.$relCoords.x - self.$sizeX / 2;
            const plate = self.$plate;
            if (StrikePlate.is(plate) && plate.$tongueBend && plate.tonguePosition === StrikeTonguePosition.right) {
                return Math.floor(maxX - plate.$minTongueHorizontalLength);
            }
            return maxX - self.$plate.$minimumDistanceHoleToEdge;
        },
        $minY: (self)=>-(self.$anchorPointTo.$absCoords.y - self.$anchorPointFrom.$relCoords.y - self.$plate.$minimumDistanceHoleToEdge - self.$sizeY / 2),
        $maxY: (self)=>self.$plate.$sizeY - self.$anchorPointTo.$absCoords.y + self.$anchorPointFrom.$relCoords.y - self.$plate.$minimumDistanceHoleToEdge - self.$sizeY / 2,
        $dimensionIndicatorColor: (self)=>{
            if (self.$plate.powderCoating) {
                return '#FFFFFF';
            }
            return '#000000';
        },
        $padding: ()=>({
                top: 0,
                right: 0,
                bottom: 0,
                left: 0
            }),
        // POSITION X INDICATOR
        $positionXIndicatorStartX: (self)=>{
            const bend = self.$plate.$activeBend;
            if (!bend || bend.orientation === BendOrientation.Horizontal || self.$absCoords.x <= bend.$middlePoint.x) {
                return -self.$absCoords.x;
            }
            return 0;
        },
        $positionXIndicatorEndX: ()=>0,
        $positionXIndicatorStartY: (self)=>self.$plate.$sizeY3D - self.$absCoords3D.y,
        $positionXIndicatorEndY: (self)=>self.$positionXIndicatorStartY,
        $positionXIndicatorStartZ: (self)=>{
            const bend = self.$plate.$activeBend;
            if (!bend) {
                return 0;
            }
            if (bend.orientation === BendOrientation.Horizontal) {
                return self.$plate.$sizeZ3D - self.$absCoords3D.z;
            } else {
                // Indicators on the start side of the bend
                if (self.$absCoords.x <= bend.$middlePoint.x) {
                    return self.$plate.thickness;
                }
                // Indicators on the end side of the bend
                return self.$plate.$sizeZ3D - self.$absCoords3D.z - (bend.direction === BendDirection.Front ? -1 : 1) * self.$plate.thickness;
            }
        },
        $positionXIndicatorEndZ: (self)=>{
            const bend = self.$plate.$activeBend;
            if (!bend) {
                return 0;
            }
            if (bend.orientation === BendOrientation.Horizontal) {
                return self.$positionXIndicatorStartZ;
            } else {
                if (self.$absCoords.x <= bend.$middlePoint.x) {
                    return self.$positionXIndicatorStartZ;
                }
                return 0;
            }
        },
        $positionXIndicatorExtrusion: (self)=>{
            const sortedXs = uniq(self.$plate.$holes.filter(Hole.is).map((h)=>h.$absCoords.x)).sort((a, b)=>a - b);
            const indicatorIndex = sortedXs.indexOf(self.$absCoords.x);
            return scaleIndicator(10 + 10 * indicatorIndex, 100 + 100 * indicatorIndex, Math.max(self.$plate.$sizeX, self.$plate.$sizeY));
        },
        $positionXIndicatorExtrusionY: (self)=>self.$plate.$activeBend?.orientation === BendOrientation.Horizontal ? 0 : self.$positionXIndicatorExtrusion,
        $positionXIndicatorExtrusionZ: (self)=>{
            if (self.$plate.$activeBend?.orientation === BendOrientation.Horizontal) {
                return self.$positionXIndicatorExtrusion * (self.$plate.$activeBend.direction === BendDirection.Front ? -1 : 1);
            } else {
                return 0;
            }
        },
        // POSITION Y INDICATOR
        $positionYIndicatorStartX: (self)=>!self.$plate.$activeBend ? self.$plate.$sizeX - self.$absCoords.x : self.$plate.$sizeX3D - self.$absCoords3D.x,
        $positionYIndicatorEndX: (self)=>self.$positionYIndicatorStartX,
        $positionYIndicatorStartY: ()=>0,
        $positionYIndicatorEndY: (self)=>{
            const bend = self.$plate.$activeBend;
            // End side of horizontal bend is
            if (bend?.orientation === BendOrientation.Horizontal && self.$absCoords.y > bend.$middlePoint.y) {
                return self.$boundingBox3D.bottomLeft.y;
            }
            return -self.$absCoords.y;
        },
        $positionYIndicatorStartZ: (self)=>{
            const bend = self.$plate.$activeBend;
            if (!bend) {
                return 0;
            }
            if (bend.orientation === BendOrientation.Horizontal && self.$absCoords.y <= bend.$middlePoint.y) {
                return self.$plate.thickness;
            }
            return self.$plate.$sizeZ3D - self.$absCoords3D.z - (bend.direction === BendDirection.Front ? -1 : 1) * self.$plate.thickness;
        },
        $positionYIndicatorEndZ: (self)=>{
            const bend = self.$plate.$activeBend;
            if (!bend) {
                return 0;
            }
            if (bend.orientation === BendOrientation.Horizontal && self.$absCoords.y > bend.$middlePoint.y) {
                return 0;
            } else {
                return self.$positionYIndicatorStartZ;
            }
        },
        $positionYIndicatorExtrusionX: (self)=>{
            const bend = self.$plate.$activeBend;
            if (!bend) {
                return self.$positionYIndicatorExtrusion;
            }
            return bend.orientation === BendOrientation.Horizontal ? self.$positionYIndicatorExtrusion : 0;
        },
        $positionYIndicatorExtrusionZ: (self)=>{
            if (self.$plate.$activeBend?.orientation === BendOrientation.Vertical) {
                return self.$positionYIndicatorExtrusion * (self.$plate.$activeBend.direction === BendDirection.Front ? -1 : 1);
            } else {
                return 0;
            }
        },
        $positionYIndicatorExtrusion: (self)=>{
            const sortedXs = uniq(self.$plate.$holes.filter(Hole.is).map((h)=>h.$absCoords.y)).sort((a, b)=>a - b);
            const indicatorIndex = sortedXs.indexOf(self.$absCoords.y);
            return scaleIndicator(10 + 10 * indicatorIndex, 100 + 100 * indicatorIndex, Math.max(self.$plate.$sizeX, self.$plate.$sizeY));
        },
        // SIZE X INDICATOR
        $sizeXIndicatorStartX: (self)=>getNominalSizeIndicatorBoundingBox3D(self).bottomLeft.x,
        $sizeXIndicatorEndX: (self)=>getNominalSizeIndicatorBoundingBox3D(self).topRight.x,
        $sizeXIndicatorStartY: (self)=>getNominalSizeIndicatorBoundingBox3D(self).topRight.y,
        $sizeXIndicatorEndY: (self)=>self.$sizeXIndicatorStartY,
        $sizeXIndicatorStartZ: (self)=>getNominalSizeIndicatorBoundingBox3D(self).bottomLeft.z,
        $sizeXIndicatorEndZ: (self)=>self.$plate.$activeBend?.orientation === BendOrientation.Vertical ? getNominalSizeIndicatorBoundingBox3D(self).topRight.z : getNominalSizeIndicatorBoundingBox3D(self).bottomLeft.z,
        $sizeXIndicatorExtrusionY: (self)=>{
            const extrusion = scaleIndicator(5, 50, Math.max(self.$plate.$sizeX, self.$plate.$sizeY));
            return getBendAwareExtrusionVector(self, {
                x: 0,
                y: extrusion,
                z: 0
            }).y;
        },
        $sizeXIndicatorExtrusionZ: (self)=>{
            const extrusion = scaleIndicator(5, 50, Math.max(self.$plate.$sizeX, self.$plate.$sizeY));
            return getBendAwareExtrusionVector(self, {
                x: 0,
                y: extrusion,
                z: 0
            }).z;
        },
        // SIZE Y INDICATOR
        $sizeYIndicatorStartX: (self)=>getNominalSizeIndicatorBoundingBox3D(self).topRight.x,
        $sizeYIndicatorEndX: (self)=>self.$sizeYIndicatorStartX,
        $sizeYIndicatorStartY: (self)=>getNominalSizeIndicatorBoundingBox3D(self).bottomLeft.y,
        $sizeYIndicatorEndY: (self)=>getNominalSizeIndicatorBoundingBox3D(self).topRight.y,
        $sizeYIndicatorStartZ: (self)=>getNominalSizeIndicatorBoundingBox3D(self).bottomLeft.z,
        $sizeYIndicatorEndZ: (self)=>{
            if (self.$plate.$activeBend?.orientation === BendOrientation.Horizontal && self.$absCoords.y > self.$plate.$activeBend.$middlePoint.y) {
                return getNominalSizeIndicatorBoundingBox3D(self).topRight.z;
            }
            return self.$sizeYIndicatorStartZ;
        },
        $sizeYIndicatorExtrusionX: (self)=>{
            const extrusion = getSizeYIndicatorExtrusion(self);
            return getBendAwareExtrusionVector(self, {
                x: extrusion,
                y: 0,
                z: 0
            }).x;
        },
        $sizeYIndicatorExtrusionZ: (self)=>{
            const extrusion = getSizeYIndicatorExtrusion(self);
            return getBendAwareExtrusionVector(self, {
                x: extrusion,
                y: 0,
                z: 0
            }).z;
        },
        $positionIndicatorLabelFontSize: (self)=>self.$plate.$indicatorLabelFontSize,
        $sizeIndicatorLabelFontSize: (self)=>scaleIndicator(4, 64, Math.max(self.$plate.$sizeX, self.$plate.$sizeY)),
        $sizeIndicatorColor: (self)=>self.$plate.$dimensionIndicatorContrastColor
    },
    // SPECIFIC HOLES
    CornerCountersinkHoles: {
        $cornerBoltHolesRecipes: memoEval((self)=>[
                self.$plate.$maxFeatureSize
            ], ($maxFeatureSize)=>generateBoltHoleRecipes($maxFeatureSize, {
                single: false,
                corner: true,
                kickPlate: false
            }))
    },
    CornerBoltHoles: {
        $cornerCountersinkHolesRecipes: memoEval((self)=>[
                self.$plate.$availableCountersinks,
                self.$plate.thickness
            ], ($availableCountersinks, thickness)=>generateCountersinkHoleRecipes($availableCountersinks, thickness, {
                single: false,
                corner: true,
                kickPlate: false
            }))
    },
    KickPlateBoltHoles: {
        $kickPlateCountersinkHolesRecipes: memoEval((self)=>[
                self.$plate.$availableCountersinks,
                self.$plate.thickness
            ], ($availableCountersinks, thickness)=>generateCountersinkHoleRecipes($availableCountersinks, thickness, {
                single: false,
                corner: false,
                kickPlate: true
            }))
    },
    KickPlateCountersinkHoles: {
        $kickPlateBoltHolesRecipes: memoEval((self)=>[
                self.$plate.$maxFeatureSize
            ], ($maxFeatureSize)=>generateBoltHoleRecipes($maxFeatureSize, {
                single: false,
                corner: false,
                kickPlate: true
            }))
    },
    PD7Hole: {
        $sizeX: (self)=>2 * (self.horizontalCenterDistance + self.horizontalRadius),
        $sizeY: (self)=>2 * (self.verticalCenterDistance + self.verticalRadius),
        $innerSize: (self)=>2 * self.primaryRadius,
        $sizeTolerance: ()=>sizeTolerance,
        $polygons: (self)=>[
                createPD7HolePolygon(self)
            ]
    },
    PN6Hole: {
        $sizeX: (self)=>self.width,
        $sizeY: (self)=>self.height,
        $innerSize: (self)=>self.height,
        $sizeTolerance: ()=>sizeTolerance,
        $polygons: (self)=>[
                createPN6HolePolygon(self)
            ]
    },
    RectangularHole: {
        $sizeX: (self)=>self.width,
        $sizeY: (self)=>self.height,
        $innerSize: (self)=>Math.min(self.width, self.height),
        $sizeTolerance: ()=>sizeTolerance,
        $polygons: (self)=>[
                createRectangularHolePolygon(self)
            ]
    },
    SquareHole: {
        $sizeX: (self)=>self.sideLength,
        $sizeY: (self)=>self.sideLength,
        $innerSize: (self)=>self.sideLength,
        $sizeTolerance: ()=>sizeTolerance,
        $polygons: (self)=>[
                createSquareHolePolygon(self)
            ]
    }
});
export const holeInvariants = implementCustomInvariants({
    Hole: {
        closeToEdge: (self)=>{
            const absX = self.$absCoords.x;
            const absY = self.$absCoords.y;
            const widthHalf = self.$sizeX / 2;
            const heightHalf = self.$sizeY / 2;
            const plate = self.$plate;
            const minEdgeDistance = plate.$minimumDistanceHoleToEdge;
            if (round(absX - widthHalf, 2) < minEdgeDistance) {
                return false;
            }
            if (round(absY - heightHalf, 2) < minEdgeDistance) {
                return false;
            }
            if (round(plate.$sizeX - absX - widthHalf, 2) < minEdgeDistance) {
                return false;
            }
            if (round(plate.$sizeY - absY - heightHalf, 2) < minEdgeDistance) {
                return false;
            }
            // Handle T-Strike specifically
            if (StrikePlate.is(plate) && plate.shapeType === StrikePlateShapeType.TStrike) {
                // Check X distance to tongue
                const xToTongueDistance = plate.tonguePosition === StrikeTonguePosition.left ? absX - plate.$tongueHorizontalLength : plate.$tStrikeMainWidth - absX;
                if (xToTongueDistance < widthHalf + minEdgeDistance) {
                    // Check Y distance to T cutout
                    const bottomEdgeY = absY - heightHalf - minEdgeDistance;
                    const topEdgeY = absY + heightHalf + minEdgeDistance;
                    if (bottomEdgeY < plate.$tongueOffsetStart || topEdgeY > plate.$sizeY - plate.$tongueOffsetEnd) {
                        return false;
                    }
                }
            }
            return true;
        },
        closeToCustomBend: (self)=>{
            const bend = self.$plate.$customBend;
            if (!bend) {
                return true;
            }
            const absX = self.$absCoords.x;
            const absY = self.$absCoords.y;
            const horizontalMargin = self.$sizeX / 2 + bend.$safeZone;
            const verticalMargin = self.$sizeY / 2 + bend.$safeZone;
            if (bend.orientation === BendOrientation.Horizontal && absY >= bend.$middlePoint.y - verticalMargin && absY <= bend.$middlePoint.y + verticalMargin) {
                return false;
            }
            if (bend.orientation === BendOrientation.Vertical && absX >= bend.$middlePoint.x - horizontalMargin && absX <= bend.$middlePoint.x + horizontalMargin) {
                return false;
            }
            return true;
        },
        closeToStrikeBend: (self)=>{
            const plate = self.$plate;
            if (!StrikePlate.is(plate)) {
                return true;
            }
            const bend = plate.$tongueBend;
            if (!bend) {
                return true;
            }
            const absX = self.$absCoords.x;
            const widthHalf = self.$sizeX / 2;
            const xToBendDistance = plate.tonguePosition === StrikeTonguePosition.left ? absX : plate.$sizeX - absX;
            if (xToBendDistance < widthHalf + plate.$minTongueHorizontalLength) {
                return false;
            }
            return true;
        },
        kickPlateFixingHolesMissing: (s)=>{
            return KickPlateFixingHoles.is(s) ? s.$horizontalCount > 0 || s.$verticalCount > 0 : true;
        }
    }
});

//# sourceMappingURL=hole.js.map